let audioCtx: AudioContext | null = null

function getAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  if (audioCtx.state === 'suspended') audioCtx.resume()
  return audioCtx
}

// Call once on first user interaction to unlock audio on iOS/iPad
export function unlockAudio() {
  const ctx = getAudioCtx()
  if (!ctx) return
  // Create a silent buffer and play it to unlock
  const buffer = ctx.createBuffer(1, 1, 22050)
  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.connect(ctx.destination)
  source.start(0)
}

export function playCorrectSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Two-tone ascending chime - use consistent timing
  const osc1 = ctx.createOscillator()
  const gain1 = ctx.createGain()
  osc1.type = 'sine'
  osc1.frequency.setValueAtTime(523.25, now) // C5
  gain1.gain.setValueAtTime(0, now)
  gain1.gain.linearRampToValueAtTime(0.3, now + 0.01)
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15)
  osc1.connect(gain1).connect(ctx.destination)
  osc1.start(now)
  osc1.stop(now + 0.15)

  const osc2 = ctx.createOscillator()
  const gain2 = ctx.createGain()
  osc2.type = 'sine'
  osc2.frequency.setValueAtTime(659.25, now + 0.1) // E5
  gain2.gain.setValueAtTime(0, now + 0.1)
  gain2.gain.linearRampToValueAtTime(0.3, now + 0.11)
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35)
  osc2.connect(gain2).connect(ctx.destination)
  osc2.start(now + 0.1)
  osc2.stop(now + 0.35)
}

export function playWrongSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'square'
  osc.frequency.setValueAtTime(200, now)
  osc.frequency.linearRampToValueAtTime(150, now + 0.2)
  gain.gain.setValueAtTime(0.2, now)
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25)
  osc.connect(gain).connect(ctx.destination)
  osc.start(now)
  osc.stop(now + 0.25)
}

export function playMatchSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Bright three-note arpeggio
  const notes = [523.25, 659.25, 783.99] // C5 E5 G5
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    const t = now + i * 0.08
    osc.frequency.setValueAtTime(freq, t)
    gain.gain.setValueAtTime(0.25, t)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2)
    osc.connect(gain).connect(ctx.destination)
    osc.start(t)
    osc.stop(t + 0.2)
  })
}

export function playMismatchSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Two descending buzzy tones
  const osc1 = ctx.createOscillator()
  const gain1 = ctx.createGain()
  osc1.type = 'sawtooth'
  osc1.frequency.setValueAtTime(300, now)
  osc1.frequency.linearRampToValueAtTime(200, now + 0.15)
  gain1.gain.setValueAtTime(0.15, now)
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.2)
  osc1.connect(gain1).connect(ctx.destination)
  osc1.start(now)
  osc1.stop(now + 0.2)

  const osc2 = ctx.createOscillator()
  const gain2 = ctx.createGain()
  osc2.type = 'sawtooth'
  osc2.frequency.setValueAtTime(250, now + 0.1)
  osc2.frequency.linearRampToValueAtTime(150, now + 0.25)
  gain2.gain.setValueAtTime(0.15, now + 0.1)
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.3)
  osc2.connect(gain2).connect(ctx.destination)
  osc2.start(now + 0.1)
  osc2.stop(now + 0.3)
}

export function playCompleteSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Victory fanfare - ascending C major arpeggio + final chord
  const notes = [523.25, 659.25, 783.99, 1046.50] // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    const t = now + i * 0.12
    osc.frequency.setValueAtTime(freq, t)
    gain.gain.setValueAtTime(0.3, t)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4)
    osc.connect(gain).connect(ctx.destination)
    osc.start(t)
    osc.stop(t + 0.4)
  })
  // Final chord
  const chordTime = now + 0.5
  const chord = [523.25, 659.25, 783.99, 1046.50]
  chord.forEach(freq => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(freq, chordTime)
    gain.gain.setValueAtTime(0.2, chordTime)
    gain.gain.exponentialRampToValueAtTime(0.01, chordTime + 0.8)
    osc.connect(gain).connect(ctx.destination)
    osc.start(chordTime)
    osc.stop(chordTime + 0.8)
  })
}

export function playGameStartSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Upbeat ascending triad with a punchy attack
  const notes = [261.63, 329.63, 392.00, 523.25] // C4 E4 G4 C5
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'triangle'
    const t = now + i * 0.08
    osc.frequency.setValueAtTime(freq, t)
    gain.gain.setValueAtTime(0.3, t)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15)
    osc.connect(gain).connect(ctx.destination)
    osc.start(t)
    osc.stop(t + 0.15)
  })
}

export function playGameEndSound() {
  const ctx = getAudioCtx()
  if (!ctx) return

  const now = ctx.currentTime
  // Descending gentle chime
  const notes = [523.25, 392.00, 329.63, 261.63] // C5 G4 E4 C4
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    const t = now + i * 0.15
    osc.frequency.setValueAtTime(freq, t)
    gain.gain.setValueAtTime(0.25, t)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3)
    osc.connect(gain).connect(ctx.destination)
    osc.start(t)
    osc.stop(t + 0.3)
  })
}

// Speak Chinese text with TTS - handles single characters better
export function speakChinese(text: string, rate = 0.8) {
  // Cancel any ongoing speech
  speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = 'zh-CN'
  utterance.rate = rate
  // For single characters, speak a bit slower for clarity
  if (text.length === 1) {
    utterance.rate = 0.6
  }
  speechSynthesis.speak(utterance)
}
